import { useState, useCallback, useRef } from 'react';
import { IPositionSummary } from '../../../../Models/IPositionSummary';

// Component Props Interface
export interface BlindTestDashboardProps {
  isModeler: boolean;
  isSme: boolean;
}

// Navigation Types
export interface NavItem {
  name: string;
  children?: NavItem[];
  isExpanded?: boolean;
  key?: string;
  onClick?: () => void;
}

export interface NavigationPath {
  industry?: string;
  subIndustry?: string;
  application?: string;
}

// State interface for the component
export interface BlindTestDashboardState {
  // Data state
  positionSummaries: IPositionSummary[];
  navItems: NavItem[];
  
  // Navigation state
  selectedIndustry?: string;
  selectedSubIndustry?: string;
  selectedApplication?: string;
  selectedSubApplication?: string;
  selectedKey: string;
  expandedItems: Record<string, boolean>;
  activeCardId: string | null;
  
  // UI state
  isLoading: boolean;
  displaySummaries: boolean;
  downloadingItems: Record<string, boolean>;
  error: string | null;
  showError: boolean;
  
  // Performance optimization
  performanceMetrics: Record<string, number>;
}

/**
 * Custom hook for managing BlindTestDashboard state
 */
export const useBlindTestDashboardState = () => {
  // Data state
  const [positionSummaries, setPositionSummaries] = useState<IPositionSummary[]>([]);
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  
  // Navigation state
  const [selectedIndustry, setSelectedIndustry] = useState<string>();
  const [selectedSubIndustry, setSelectedSubIndustry] = useState<string>();
  const [selectedApplication, setSelectedApplication] = useState<string>();
  const [selectedSubApplication, setSelectedSubApplication] = useState<string>();
  const [selectedKey, setSelectedKey] = useState("");
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  
  // UI state
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [displaySummaries, setDisplaySummaries] = useState(false);
  const [downloadingItems, setDownloadingItems] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  
  // Performance optimization - use useRef to avoid re-renders
  const performanceMetricsRef = useRef<Record<string, number>>({});
  const [performanceMetrics, setPerformanceMetrics] = useState<Record<string, number>>({});
  
  /**
   * Log performance metrics - uses useRef to avoid re-renders
   */
  const logPerformance = useCallback((label: string, startTime: number) => {
    const duration = performance.now() - startTime;
    // Store in ref to avoid re-renders - no state updates
    performanceMetricsRef.current = {
      ...performanceMetricsRef.current,
      [label]: duration
    };
    
    // Log to console for development debugging (can be removed in production)
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[Performance] ${label}: ${duration.toFixed(2)}ms`);
    }
  }, []); // Empty dependency array - function is stable
  
  /**
   * Reset navigation state - memoized to prevent re-creation
   */
  const resetNavigationState = useCallback(() => {
    setSelectedIndustry(undefined);
    setSelectedSubIndustry(undefined);
    setSelectedApplication(undefined);
    setSelectedSubApplication(undefined);
    setSelectedKey("");
    setExpandedItems({});
    setActiveCardId(null);
    setDisplaySummaries(false);
  }, []);
  
  /**
   * Set navigation selection - memoized to prevent re-creation
   */
  const setNavigationSelection = useCallback((
    industry: string,
    subIndustry: string,
    application: string,
    subApplication: string
  ) => {
    setSelectedIndustry(industry);
    setSelectedSubIndustry(subIndustry);
    setSelectedApplication(application);
    setSelectedSubApplication(subApplication);
    setSelectedKey(subApplication);
    setDisplaySummaries(true);
    setActiveCardId(null); // Reset active card when selection changes
  }, []);
  
  /**
   * Set active card with default selection logic - memoized to prevent re-creation
   */
  const setActiveCardWithDefault = useCallback((summaries: IPositionSummary[]) => {
    if (summaries.length > 0) {
      // Default to first card with "ToDo" or the first card overall
      const defaultCard =
        summaries.find((item) => item.summary["todo"] > 0) || 
        summaries[0];
        
      if (defaultCard) {
        const cardId = `${defaultCard.alfaLavalPosition}-${defaultCard.productLine}-${defaultCard.blindTestDataSetId}`;
        setActiveCardId(cardId);
      }
    }
  }, []);
  
  return {
    // Data state
    positionSummaries,
    setPositionSummaries,
    navItems,
    setNavItems,
    
    // Navigation state
    selectedIndustry,
    setSelectedIndustry,
    selectedSubIndustry,
    setSelectedSubIndustry,
    selectedApplication,
    setSelectedApplication,
    selectedSubApplication,
    setSelectedSubApplication,
    selectedKey,
    setSelectedKey,
    expandedItems,
    setExpandedItems,
    activeCardId,
    setActiveCardId,
    
    // UI state
    isLoading,
    setIsLoading,
    displaySummaries,
    setDisplaySummaries,
    downloadingItems,
    setDownloadingItems,
    error,
    setError,
    showError,
    setShowError,
    
    // Performance optimization
    performanceMetrics,
    setPerformanceMetrics,
    logPerformance,
    
    // Helper methods
    resetNavigationState,
    setNavigationSelection,
    setActiveCardWithDefault
  };
}; 